import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { categoryName, formatDate } from "@/utils/articles";

interface HeroCarouselProps {
  items: Article[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const timer = useRef<number | null>(null);

  const go = (next: number) => {
    setIndex((next + items.length) % items.length);
  };

  useEffect(() => {
    if (items.length <= 1) return;
    timer.current = window.setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
    }, 6000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [items.length]);

  if (items.length === 0) return null;
  const active = items[index];

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-950">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.heroImage ?? item.cover}
            alt={item.title}
            title={`${item.title} - 理论电影`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/45 to-primary-950/25" />
        </div>
      ))}

      <div className="relative h-full max-w-[1280px] mx-auto px-4 md:px-8 flex flex-col justify-end pb-14 md:pb-20">
        <div key={active.id} className="max-w-3xl animate-fade-up">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-semibold">
              {categoryName(active.category)}
            </span>
            <span className="text-xs text-background-200 tracking-wide">
              {formatDate(active.date)} · {active.author}
            </span>
          </div>
          <h2 className="font-heading text-2xl md:text-5xl font-bold text-background-50 leading-tight mb-4">
            <Link to={`/detail/${active.id}`} className="hover:text-accent-300 transition-colors">
              {active.title}
            </Link>
          </h2>
          <p className="hidden md:block text-base text-background-200 leading-relaxed mb-7 max-w-2xl line-clamp-2">
            {active.summary}
          </p>
          <div className="flex items-center gap-4">
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-2 px-5 md:px-6 py-2.5 rounded-md bg-secondary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-secondary-600 transition-colors"
            >
              阅读全文
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to={active.category === "theory" ? "/category/theory" : active.category === "review" ? "/category/review" : "/category/culture"}
              className="inline-flex items-center gap-2 text-sm text-background-100 hover:text-accent-300 transition-colors"
            >
              查看专题
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-8 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>

      <button
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer backdrop-blur"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 text-background-50 hover:bg-background-50/30 transition-colors cursor-pointer backdrop-blur"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>
    </section>
  );
}